/* eslint-disable unused-imports/no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApplicationConstants } from '@sa/app/shared/constants/ApplicationConstants';
import { UrlEncoder } from '@sa/app/shared/utility/url-encoder';
import { CommonService } from '@sa/app/shared/services/common.service';
import { DeeplinkService } from '@sa/app/shared/services/deeplink.service';
import { EnvironmentLogger } from '@sa/app/core/utils/environment-logger.util';
import { inject } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class WorkitemLinkGuard implements CanActivate {
  commonServiceData: any;
  commonService = inject(CommonService);
  private deeplinkService = inject(DeeplinkService);
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | Observable<boolean> {
    // WAF-safe token deep-links: /deliverable?token={uuid}&tenant={tenantGuid}.
    // The original payload is stored server-side and resolved via the API; the resolved
    // payload is the SAME object the legacy `?data=` path produces.
    const tokenParam = route.queryParams['token'];
    if (tokenParam) {
      const tenant = route.queryParams['tenant'];
      // The resolve endpoint is tenant-scoped (the token lives in the tenant's
      // Client DB), so a token without a tenant cannot be resolved — block early.
      if (!tenant) {
        EnvironmentLogger.warn(
          'WorkitemLinkGuard: deep-link token present but tenant missing; blocking navigation.'
        );
        return false;
      }
      EnvironmentLogger.debug(
        'WorkitemLinkGuard',
        'Resolving deep-link token for tenant',
        tenant
      );
      return this.deeplinkService.resolveToken(tenant, tokenParam).pipe(
        map(response => {
          // Token payload is RAW JSON (not base64) — parse directly.
          const payload = JSON.parse(response.payload);
          this.applyDeeplinkPayload(payload);
          EnvironmentLogger.debug(
            'WorkitemLinkGuard',
            'Deep-link token resolved; navigation allowed.'
          );
          return true;
        }),
        // Unknown/invalid token (404) or any failure blocks navigation, matching the
        // legacy data-path `catch { return false; }` behavior.
        catchError(() => {
          EnvironmentLogger.warn(
            'WorkitemLinkGuard: deep-link token resolution failed; blocking navigation.'
          );
          return of(false);
        })
      );
    }

    const dataParam = route.queryParams['data'];
    if (!dataParam) {
      return true;
    }

    try {
      const payload = UrlEncoder.decode(dataParam);

      // UrlBuilder format (focus-area / work-detail escalation links) carries
      // workspaceGuid/workspaceItemGuid/tenantId/entityId directly — no projectdetails wrapper.
      // The destination component reads the `data` param itself, so just allow through.
      if (payload.projectdetails === undefined) {
        return true;
      }

      this.applyDeeplinkPayload(payload);
    } catch {
      return false;
    }

    return true;
  }

  /**
   * Builds and persists the session state for a resolved deep-link payload.
   * Shared by both the WAF-safe token path and the legacy `?data=` path — the mapping
   * is identical once `{ workspaceItemType, projectdetails }` is available.
   * @param payload The deep-link payload — `{ workspaceItemType, projectdetails }`, where
   * `projectdetails` is a JSON string parsed below into the per-item context (tenant, entity, etc.).
   */
  private applyDeeplinkPayload(payload: {
    workspaceItemType: unknown;
    projectdetails: string;
  }): void {
    sessionStorage.setItem(
      ApplicationConstants.SessionKeyConstants.WorkspaceItemType,
      JSON.stringify(payload.workspaceItemType)
    );
    const projectDetailsData = JSON.parse(payload.projectdetails);
    sessionStorage.setItem(
      ApplicationConstants.SessionKeyConstants.TenantId,
      JSON.stringify(projectDetailsData.TenantGuid)
    );
    sessionStorage.setItem(
      ApplicationConstants.SessionKeyConstants.LegalEntityId,
      JSON.stringify(projectDetailsData.LegalEntityGuid)
    );
    sessionStorage.setItem(
      ApplicationConstants.SessionKeyConstants.TenantGuid,
      JSON.stringify(projectDetailsData.TenantGuid)
    );
    sessionStorage.setItem(
      ApplicationConstants.SessionKeyConstants.WorkspaceGuid,
      JSON.stringify(projectDetailsData.WorkspaceGuid)
    );
    sessionStorage.setItem(
      ApplicationConstants.SessionKeyConstants.WorkspaceItemGuid,
      JSON.stringify(projectDetailsData.WorkspaceItemGuid)
    );
    sessionStorage.setItem(
      ApplicationConstants.SessionKeyConstants.ParentGuid,
      JSON.stringify(projectDetailsData.ParentGuid)
    );
    sessionStorage.setItem('isDeeplink', 'true');

    // Extract only necessary properties to avoid circular references
    const selectedEntity = this.commonService.getState('selectedEntity');
    const selectedCountry = this.commonService.getState('selectedCountry');
    const selectedFiscalYear = this.commonService.getState('selectedFiscalYear');
    const selectedPeriod = this.commonService.getState('selectedPeriod');
    const projectDetails = this.commonService.getState('projectDetails');
    const selectedClientDetails = this.commonService.getState('selectedClientDetails');

    this.commonServiceData = {
      tenantGuid: projectDetailsData.TenantGuid,
      selectedEntity: {
        entityId:  selectedEntity?.entityId ?? null,
        entityGuid: selectedEntity?.entityGuid ?? null,
        entityName: projectDetailsData.EntityName ?? selectedEntity?.entityName ?? null,
        entityCode: projectDetailsData.EntityCode ?? selectedEntity?.entityCode ?? null,
        entityShortName: selectedEntity?.entityShortName ?? null,
        countryGuid: selectedEntity?.countryGuid ?? null,
        countryId: selectedEntity?.countryId ?? null,
        isActive: selectedEntity?.isActive ?? true,
        entityDetails: selectedEntity?.entityDetails ?? null
      },
      selectedCountry: {
        countryId:  selectedCountry?.countryId ?? null,
        countryGuid:  selectedCountry?.countryGuid ?? null,
        countryName: projectDetailsData.CountryName ?? selectedCountry?.countryName ?? null,
        isActive: selectedCountry?.isActive ?? true,
        regionId: selectedCountry?.regionId ?? null
      },
      selectedFiscalYear: {
        fiscalYearId:  selectedFiscalYear?.fiscalYearId ?? null,
        fiscalYearGuid:  selectedFiscalYear?.fiscalYearGuid ?? null,
        auditYearName:  selectedFiscalYear?.auditYearName ?? null,
        auditYear: projectDetailsData.FilingYear ?? selectedFiscalYear?.auditYear ?? null,
        isActive: selectedFiscalYear?.isActive ?? true
      },
      selectedPeriod: {
        periodId:  selectedPeriod?.periodId ?? null,
        periodName: projectDetailsData.Period ?? selectedPeriod?.periodName ?? null,
        isActive: selectedPeriod?.isActive ?? true,
        periodGuid: selectedPeriod?.periodGuid ?? null
      },
      selectedProject: {
        projectGuid: projectDetailsData.ProjectGuid ?? projectDetails?.projectGuid ?? null,
        isProjectExists: projectDetails?.isProjectExists ?? null,
        loggedInUserTaskCount: projectDetails?.loggedInUserTaskCount ?? null
      },
      selectedClientDetails: selectedClientDetails ? {
        tenantGuid: selectedClientDetails.tenantGuid,
        jurisdictionGuid: selectedClientDetails.jurisdictionGuid,
        legalEntityGuid: selectedClientDetails.legalEntityGuid,
        fiscalYearGuid: selectedClientDetails.fiscalYearGuid
      } : null,
      selectedClient: projectDetailsData.ClientName ?? null
    };
    sessionStorage.setItem('commonServiceData', JSON.stringify(this.commonServiceData));
  }
}
